/**
 * Markdown ↔ HTML conversion for the Knowledge Base rich-text editor.
 *
 * The knowledge base text is sent verbatim to Bland AI's knowledge store
 * (`type: 'text'`), so it must stay readable as plain text — we persist
 * markdown, never HTML. The tiptap editor works in HTML, so we convert at the
 * component boundary. The node set the editor supports is handled:
 * headings (`#`–`###`), paragraphs, **bold**, *italic*, `- ` bullet lists,
 * and `---` horizontal rules. Anything else passes through as plain
 * paragraph text. Headings deeper than `###` are clamped to `###` (the
 * editor's deepest level).
 */

/**
 * Maximum knowledge base length in characters (of the persisted markdown).
 * Keeps the content within a size Bland AI ingests reliably and guards
 * against runaway pastes.
 */
export const KNOWLEDGE_BASE_MAX_LENGTH = 50_000

const MAX_HEADING_LEVEL = 3

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const unescapeHtml = (text: string): string =>
  text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#x27;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')

/** Converts inline markdown (bold/italic) in an escaped-HTML-safe line. */
const inlineMarkdownToHtml = (text: string): string =>
  escapeHtml(text)
    // Bold first so `**` isn't consumed by the italic rule.
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')

/** Converts inline HTML (strong/em/b/i) back to markdown and strips other tags. */
const inlineHtmlToMarkdown = (html: string): string =>
  unescapeHtml(
    html
      .replace(/<(strong|b)>(.*?)<\/\1>/g, '**$2**')
      .replace(/<(em|i)>(.*?)<\/\1>/g, '*$2*')
      .replace(/<br\s*\/?>/g, '\n')
      // Strip any remaining tags rather than leaking them into the markdown.
      .replace(/<[^>]+>/g, '')
  )

/**
 * Converts knowledge-base markdown to HTML for the tiptap editor.
 * `#`–`###` lines become headings (deeper levels clamp to `###`), `---`
 * lines become horizontal rules, and consecutive `- ` lines become a bullet
 * list. Consecutive plain lines merge into a single paragraph joined with
 * <br> so single newlines round-trip faithfully; blank lines separate
 * paragraphs.
 */
export const markdownToHtml = (markdown: string): string => {
  if (!markdown?.trim()) return ''

  const lines = markdown.split('\n')
  const blocks: string[] = []
  let listItems: string[] = []
  let paragraphLines: string[] = []

  const flushList = () => {
    if (listItems.length > 0) {
      blocks.push(`<ul>${listItems.join('')}</ul>`)
      listItems = []
    }
  }

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      blocks.push(`<p>${paragraphLines.join('<br>')}</p>`)
      paragraphLines = []
    }
  }

  for (const line of lines) {
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/)
    if (headingMatch) {
      flushParagraph()
      flushList()
      const level = Math.min(headingMatch[1].length, MAX_HEADING_LEVEL)
      blocks.push(
        `<h${level}>${inlineMarkdownToHtml(headingMatch[2])}</h${level}>`
      )
      continue
    }

    if (/^\s*-{3,}\s*$/.test(line)) {
      flushParagraph()
      flushList()
      blocks.push('<hr>')
      continue
    }

    const bulletMatch = line.match(/^\s*[-*]\s+(.*)$/)
    if (bulletMatch) {
      flushParagraph()
      listItems.push(`<li><p>${inlineMarkdownToHtml(bulletMatch[1])}</p></li>`)
      continue
    }
    flushList()
    if (line.trim()) {
      paragraphLines.push(inlineMarkdownToHtml(line))
    } else {
      flushParagraph()
    }
  }
  flushParagraph()
  flushList()

  return blocks.join('')
}

/**
 * Converts tiptap HTML output back to knowledge-base markdown.
 * Headings become `#`–`###` lines, horizontal rules become `---`,
 * paragraphs become lines separated by blank lines, and bullet lists become
 * `- ` lines.
 */
export const htmlToMarkdown = (html: string): string => {
  if (!html?.trim()) return ''

  const blocks: string[] = []
  // Match top-level heading/p/ul/hr blocks in document order.
  const blockRegex =
    /<h([1-6])>([\s\S]*?)<\/h\1>|<p>([\s\S]*?)<\/p>|<ul>([\s\S]*?)<\/ul>|<hr\s*\/?>/g
  let match: RegExpExecArray | null

  while ((match = blockRegex.exec(html)) !== null) {
    const [full, headingLevel, heading, paragraph, list] = match
    if (headingLevel !== undefined) {
      const text = inlineHtmlToMarkdown(heading).trim()
      if (text) blocks.push(`${'#'.repeat(Number(headingLevel))} ${text}`)
    } else if (paragraph !== undefined) {
      const text = inlineHtmlToMarkdown(paragraph).trim()
      if (text) blocks.push(text)
    } else if (list !== undefined) {
      const items = [...list.matchAll(/<li>([\s\S]*?)<\/li>/g)]
        .map(([, item]) => `- ${inlineHtmlToMarkdown(item).trim()}`)
        .join('\n')
      if (items) blocks.push(items)
    } else if (full.startsWith('<hr')) {
      blocks.push('---')
    }
  }

  // Fallback: html without recognizable blocks (shouldn't happen with tiptap)
  if (blocks.length === 0) {
    const text = inlineHtmlToMarkdown(html).trim()
    return text
  }

  return blocks.join('\n\n')
}
